import { useQuery } from "@apollo/client";
import SideSheetModal from "@/components/SideSheetModal";
import TaskSideSheetContent from "./TaskSideSheetContent";
import TaskSideSheetSkeleton from "./TaskSideSheetSkeleton";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import { GET_TASK_QUERY } from "./queries";
import { type Path, useNavigate } from "@/router";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";

export type TaskSideSheetProps = {
  open: boolean;
  onClose: () => void;
  taskId: string | null;
  title?: string;
};

export default function TaskSideSheet({ open, onClose, taskId, title = "Task Details" }: TaskSideSheetProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const { data, loading, error } = useQuery(GET_TASK_QUERY, {
    variables: { id: taskId },
    fetchPolicy: "cache-first",
    skip: !taskId || !open
  });

  const task = data?.task;

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  function handleViewTask() {
    if (taskId) {
      if (task) {
        trackEvent(TASK_ANALYTICS_EVENTS.opened, {
          ...getTaskAnalyticsData(task, "task_side_sheet"),
          openSource: "view_task_button"
        });
      }
      onClose();
      navigate(`/tasks/${taskId}` as Path);
    }
  }

  return (
    <SideSheetModal isSidebarOpen={open} setSidebarOpen={setIsSideSheetOpen} title={title} actions={null}>
      {loading ? (
        <TaskSideSheetSkeleton />
      ) : error ? (
        <ErrorBoundary message={error.message} variant="inline" />
      ) : task ? (
        <TaskSideSheetContent task={task} onViewTask={handleViewTask} />
      ) : (
        <EmptyState iconName="inventory" title="Task Not Found" />
      )}
    </SideSheetModal>
  );
}
